// TRF Import — reads a FIDE Tournament Report File back into the app's tournament shape.
//
// The inverse of js/services/trf-export.js, and deliberately more forgiving than it: we
// write one dialect, but we have to read every dialect an arbiter might be handed. The
// files this was built against are our own TRF16/TRF26 output, TurneringsService 6.9, and
// Swiss-Manager — which disagree about opponent padding ('0007' vs '   7'), colour case
// ('W' vs 'w'), name spelling ('Last, First' vs 'Last,First') and year-only birth dates
// ('1990', '1990/00/00', blank). All of those read the same here.
//
// Reading is by absolute column, exactly as writing is, because the format is fixed-width
// and a name containing two spaces is not a column boundary. The one thing never taken
// from the file is a player's SCORE: the points column (81-84) is recomputed by the app's
// own standings engine from the results, so an import cannot introduce a total that
// disagrees with the games it came from. Where the file's own total disagrees, that is
// reported as a warning rather than silently adopted or silently dropped.
//
// Classic <script>, like trf-export.js, so index.html can load it with a plain tag and
// tests/helpers/load-classic-script.js can pull it into a sandbox.

const TRFImport = {

    // ---- low level helpers -------------------------------------------------

    /**
     * Characters from a 1-indexed column range, tolerating a line that stops early —
     * exporters trim trailing spaces, so the last round's columns are routinely absent.
     */
    seg(line, col, width) {
        return (line || '').substring(col - 1, col - 1 + width);
    },

    segTrim(line, col, width) {
        return this.seg(line, col, width).trim();
    },

    int(str, dflt) {
        const n = parseInt(String(str == null ? '' : str).trim(), 10);
        return Number.isFinite(n) ? n : (dflt === undefined ? 0 : dflt);
    },

    /**
     * A TRF record code is three digits followed by a space. The test has to be this
     * strict: the Swiss-Manager files carry a column ruler ('1234567890123...') that
     * starts with digits, and a header row of field letters, neither of which is a record.
     */
    recordCode(line) {
        return /^\d{3}[ \t]/.test(line || '') ? line.substring(0, 3) : null;
    },

    /**
     * Is this text a TRF at all? Used to pick a parser when a file arrives with an
     * uninformative name — .txt is as common as .trf in practice. A TRF has to have at
     * least one player line; the tournament records alone could be almost anything.
     */
    looksLikeTRF(text) {
        if (!text) return false;
        const lines = String(text).split(/\r?\n/);
        let hasPlayer = false;
        for (const line of lines) {
            if (/^\s*</.test(line)) return false;   // XML: this is a TRX
            if (this.recordCode(line) === '001') hasPlayer = true;
        }
        return hasPlayer;
    },

    // ---- field decoding ----------------------------------------------------

    /** '2026/08/02' (or '2026-08-02') → '2026-08-02'. Anything else → ''. */
    parseDate(str) {
        const m = String(str || '').trim().match(/^(\d{4})[/-](\d{2})[/-](\d{2})$/);
        if (!m) return '';
        // Swiss-Manager writes an unknown month/day as 00; that is a year, not a date.
        if (m[2] === '00' || m[3] === '00') return '';
        return `${m[1]}-${m[2]}-${m[3]}`;
    },

    /**
     * The birth field (columns 70-79) in every spelling seen in the wild: a full
     * YYYY/MM/DD, Swiss-Manager's year-only YYYY/00/00, and the bare YYYY that older
     * versions of our own exporter wrote.
     */
    parseBirth(str) {
        const raw = String(str || '').trim();
        if (!raw) return { birthDate: '', birthYear: '' };
        const full = this.parseDate(raw);
        if (full) return { birthDate: full, birthYear: full.substring(0, 4) };
        const y = raw.match(/^(\d{4})/);
        return { birthDate: '', birthYear: y ? y[1] : '' };
    },

    sexToGender(ch) {
        const c = String(ch || '').trim().toLowerCase();
        if (c === 'm') return 'm';
        if (c === 'w' || c === 'f') return 'w';
        return '';
    },

    /**
     * 'Carlsen, Magnus' → { lastName: 'Carlsen', givenName: 'Magnus' }.
     * Swiss-Manager omits the space after the comma; a name with no comma at all is taken
     * as a surname, which is what a mononym is.
     */
    splitName(field) {
        const raw = String(field || '').trim();
        if (!raw) return { lastName: '', givenName: '' };
        const i = raw.indexOf(',');
        if (i === -1) return { lastName: raw, givenName: '' };
        return {
            lastName: raw.substring(0, i).trim(),
            givenName: raw.substring(i + 1).trim()
        };
    },

    /**
     * Encoded time control back to the '90+30' shape the tournament form uses.
     * '5400+30' → '90+30', '900+10' → '15+10'. A base that is not whole minutes stays in
     * seconds rather than being rounded into a lie.
     */
    decodeTimeControl(str) {
        const s = String(str || '').trim();
        const m = s.match(/^(\d+)(?:\+(\d+))?$/);
        if (!m) return s;                          // multi-period or Armageddon: keep verbatim
        const base = parseInt(m[1], 10);
        const incr = m[2] != null ? parseInt(m[2], 10) : null;
        const mins = (base >= 60 && base % 60 === 0) ? base / 60 : base;
        return incr != null ? `${mins}+${incr}` : String(mins);
    },

    /**
     * Record 122 is free text and every program writes it differently, from '15+10' to a
     * whole sentence. Pull a 'minutes+increment' out of it when one is recognisable, so
     * the tournament form gets a value it can actually show in its input.
     */
    timeControlFromText(str) {
        const s = String(str || '').trim();
        if (!s) return '';
        if (/^\d+(\+\d+)?$/.test(s)) return s;
        const m = s.match(/(\d+)\s*(?:min(?:ut)?(?:es|ter)?)?\s*\+\s*(\d+)/i);
        return m ? `${m[1]}+${m[2]}` : s;
    },

    /**
     * Record 102/112 carry an arbiter name, sometimes with a FIDE ID appended in a later
     * column ('Lasse Ostebo Lovik                   1505033') and sometimes comma-separated
     * with a federation ('IA Sorokina Anastasia, AUS, 13501151'). The app has one free-text
     * arbiter field, so keep the name and drop the identifiers.
     */
    parseArbiter(str) {
        let s = String(str || '').trim();
        if (!s) return '';
        s = s.replace(/\s{2,}\d{5,12}$/, '');        // trailing ID in its own column
        s = s.replace(/\s*,\s*[A-Z]{3}\s*,\s*\d{5,12}$/, ''); // ', AUS, 13501151'
        return s.trim();
    },

    /**
     * The FIDE ID parseArbiter() strips off the name — kept rather than discarded, because
     * both file formats have somewhere to put it back (TRF record 102 columns 38-48, TRX
     * ArbiterFideId) and an arbiter identified in the source should still be identified
     * after a round trip through this app.
     */
    parseArbiterId(str) {
        const s = String(str || '').trim();
        if (!s) return '';
        const tail = s.match(/\s{2,}(\d{5,12})$/) ||                 // ID in its own column
                     s.match(/,\s*[A-Z]{3}\s*,\s*(\d{5,12})$/);      // ', AUS, 13501151'
        return tail ? tail[1] : '';
    },

    /** '1.0' → '1', '0.5' → '0.5' — the spelling the point-system dropdown uses. */
    trimPointValue(n) {
        if (!Number.isFinite(n)) return '';
        return String(Math.round(n * 100) / 100);
    },

    /**
     * Record 162 — the point values, read from the same columns trf-export.js writes:
     * a letter at 6/15/24/33/42/51 with its value in the next four. Only W, D and L feed
     * the app's point system; the bye values are implied by it.
     */
    parseScoringSystem(line) {
        const found = {};
        for (let i = 0; i < 6; i++) {
            const col = 6 + i * 9;
            const letter = this.segTrim(line, col, 1).toUpperCase();
            const value = parseFloat(this.segTrim(line, col + 1, 4));
            if (letter && Number.isFinite(value)) found[letter] = value;
        }
        if (!Number.isFinite(found.W) || !Number.isFinite(found.D)) return '';
        const loss = Number.isFinite(found.L) ? found.L : 0;
        return [found.W, found.D, loss].map(v => this.trimPointValue(v)).join('-');
    },

    /**
     * Record 192 — the FIDE tournament-type code, to one of the app's three formats.
     * CUSTOM_SWISS is what trf-export.js writes for Monrad, which is the only non-FIDE
     * system the app pairs, so that is the reading it gets back.
     */
    formatFromTypeCode(code) {
        const c = String(code || '').trim().toUpperCase();
        if (!c) return '';
        if (c.indexOf('ROUNDROBIN') !== -1 || c.indexOf('ROUND_ROBIN') !== -1) return 'round-robin';
        if (c === 'CUSTOM_SWISS') return 'monrad';
        if (c.indexOf('SWISS') !== -1 || c.indexOf('DUTCH') !== -1 ||
            c.indexOf('DUBOV') !== -1 || c.indexOf('BURSTEIN') !== -1) return 'swiss';
        return '';
    },

    /**
     * Record 092 is free text, but it is the only place the rate of play (standard /
     * rapid / blitz) is recorded at all, and trf-export.js writes both halves into it as
     * 'Swiss System (Rapid)'. Read back whatever of the two is recognisable.
     */
    parseTypeText(str) {
        const s = String(str || '').toLowerCase();
        const out = { format: '', type: '' };
        if (/round.?robin/.test(s)) out.format = 'round-robin';
        else if (/monrad/.test(s)) out.format = 'monrad';
        else if (/swiss|dutch/.test(s)) out.format = 'swiss';
        if (/blitz|lyn/.test(s)) out.type = 'blitz';
        else if (/rapid|hurtig/.test(s)) out.type = 'rapid';
        else if (/standard|klassisk/.test(s)) out.type = 'standard';
        return out;
    },

    /**
     * Rate of play inferred from the time control, for files whose 092 does not say.
     * FIDE thresholds: a game where each player's base plus 60x the increment is over an
     * hour is standard, 10 minutes or under is blitz, and rapid is what lies between.
     */
    typeFromTimeControl(tc) {
        const m = String(tc || '').trim().match(/^(\d+)(?:\+(\d+))?$/);
        if (!m) return '';
        const minutes = parseInt(m[1], 10) + (m[2] ? parseInt(m[2], 10) : 0);
        if (minutes <= 10) return 'blitz';
        if (minutes < 60) return 'rapid';
        return 'standard';
    },

    /**
     * Rank-Order-Descriptor back to the app's own tie-break code. This is the inverse of
     * the LEGACY table in js/core/tiebreak.js, and it matters more than it looks:
     * SwissEngine.calculateStandings — which is what actually orders the standings —
     * compares against these lowercase codes ('de', 'bh1', ...), not against descriptors.
     * A descriptor it does not recognise falls through to its rating fallback, so an
     * imported tournament would rank differently from an identical one set up by hand.
     * Descriptors with no legacy equivalent are kept as-is: that is the vocabulary the
     * tie-break editor itself stores, so they are no worse off than a hand-picked one.
     */
    DESCRIPTOR_TO_APP_CODE: {
        DE: 'de', 'BH/C1': 'bh1', BH: 'bh', SB: 'sb', WIN: 'wins', ARO: 'aor', TPR: 'pr'
    },

    /**
     * Records 202 and 212 both list Rank-Order-Descriptors, and both mean the same thing
     * to the app's tie-break engine. They differ only in whether points are stated: 212
     * spells PTS out, 202 leaves it implied. Either way the app's tie-break list starts
     * AFTER points, so PTS is dropped when present rather than stored as a tie-break.
     */
    parseTiebreaks(str) {
        return String(str || '')
            .split(/[,\s]+/)
            .map(s => s.trim().toUpperCase())
            .filter(s => s && s !== 'PTS')
            .map(s => this.DESCRIPTOR_TO_APP_CODE[s] || s);
    },

    // ---- result codes ------------------------------------------------------

    /**
     * A round cell's result code to what it meant for the player whose line it is.
     * The played/unplayed distinction matters: '+' and '-' are a forfeit, which the app
     * spells '+/-' and the pairing rules treat as a game that never happened.
     */
    outcome(code) {
        switch (String(code || ' ').toUpperCase()) {
            case '1': case 'W': return 'win';
            case '0': case 'L': return 'loss';
            case '=': case 'D': return 'draw';
            case '+': case 'F': case 'U': return 'forfeitWin';
            case '-': return 'forfeitLoss';
            case 'H': return 'halfBye';
            case 'Z': case 'A': return 'zeroBye';
            default: return 'none';
        }
    },

    /**
     * The app's result string for a game, from both players' cells. White's cell decides;
     * black's is consulted only to tell a double forfeit ('-' on both sides, which the app
     * calls '-/-') from an ordinary forfeit loss, and to fill in for a white cell that was
     * left blank.
     */
    resultFor(whiteCode, blackCode) {
        const w = this.outcome(whiteCode);
        const b = this.outcome(blackCode);
        switch (w) {
            case 'win': return '1-0';
            case 'loss': return '0-1';
            case 'draw': return '0.5-0.5';
            case 'forfeitWin': return '+/-';
            case 'forfeitLoss': return b === 'forfeitLoss' ? '-/-' : '-/+';
            default: break;
        }
        // White's cell says nothing — mirror black's, if black's says anything.
        switch (b) {
            case 'win': return '0-1';
            case 'loss': return '1-0';
            case 'draw': return '0.5-0.5';
            case 'forfeitWin': return '-/+';
            case 'forfeitLoss': return '+/-';
            default: return '';
        }
    },

    // ---- parsing -----------------------------------------------------------

    /**
     * Read the round cells off a player line. Returns one entry per round column that
     * carries anything at all; a wholly blank column is a round this player has no entry
     * for, which is not the same as a zero-point bye and must not become one.
     */
    parseCells(line) {
        const cells = [];
        // Round columns run from 92 in steps of 10 for as long as the line does.
        for (let ri = 0; ; ri++) {
            const base = 92 + ri * 10;
            if (base > (line || '').length) break;
            const oppRaw = this.segTrim(line, base, 4);
            const colour = this.segTrim(line, base + 5, 1).toLowerCase();
            const result = this.segTrim(line, base + 7, 1).toUpperCase();
            if (!oppRaw && !colour && !result) { cells.push(null); continue; }
            cells.push({
                oppNo: this.int(oppRaw, 0),
                colour: colour === 'w' ? 'w' : (colour === 'b' ? 'b' : '-'),
                result: result || ' '
            });
        }
        while (cells.length && cells[cells.length - 1] === null) cells.pop();
        return cells;
    },

    parsePlayerLine(line, index) {
        const name = this.splitName(this.seg(line, 15, 33));
        const birth = this.parseBirth(this.seg(line, 70, 10));
        const startNo = this.int(this.seg(line, 5, 4), 0);
        return {
            startNo: startNo || (index + 1),
            gender: this.sexToGender(this.seg(line, 10, 1)),
            title: this.segTrim(line, 11, 3).toUpperCase(),
            lastName: name.lastName,
            givenName: name.givenName,
            rating: this.int(this.seg(line, 49, 4), 0),
            fed: this.segTrim(line, 54, 3).toUpperCase(),
            fideId: this.segTrim(line, 58, 11),
            birthDate: birth.birthDate,
            birthYear: birth.birthYear,
            // Kept only to check the results add up — never written into the app's state.
            statedPoints: parseFloat(this.segTrim(line, 81, 4)),
            cells: this.parseCells(line)
        };
    },

    /**
     * Parse TRF text into { meta, players, rounds, warnings } using the app's own field
     * names. Throws only when the file is not a tournament at all; everything short of
     * that — an opponent who does not exist, two players who disagree about their colours,
     * a stated score that does not match the games — is a warning, because an arbiter with
     * a slightly broken file is much better served by an import they can then fix than by
     * a refusal.
     */
    parse(text) {
        const warnings = [];
        const lines = String(text || '').split(/\r?\n/);

        const records = {};      // last-wins, except 112 which repeats
        const playerLines = [];
        lines.forEach(line => {
            const code = this.recordCode(line);
            if (!code) return;
            if (code === '001') playerLines.push(line);
            else if (!(code in records)) records[code] = line;
        });

        if (playerLines.length === 0) {
            throw new Error('No player records (001) found — this does not look like a TRF file.');
        }

        const body = code => (records[code] ? records[code].substring(4).replace(/\s+$/, '') : '');

        const players = playerLines.map((l, i) => this.parsePlayerLine(l, i));

        // Two players sharing a starting number would make every reference to either of
        // them ambiguous, so the later one is renumbered rather than silently shadowing.
        const byStartNo = {};
        players.forEach(p => {
            if (byStartNo[p.startNo]) {
                const next = Math.max(...players.map(q => q.startNo)) + 1;
                warnings.push(`Two players share starting number ${p.startNo}; "${p.lastName}" was renumbered to ${next}.`);
                p.startNo = next;
            }
            byStartNo[p.startNo] = p;
        });

        players.forEach((p, i) => { p.id = 'p_trf_' + (i + 1) + '_' + p.startNo; });

        // ---- tournament records --------------------------------------------
        const typeText = this.parseTypeText(body('092'));
        const encodedTC = this.decodeTimeControl(body('222'));
        const textTC = this.timeControlFromText(body('122'));
        const timeControl = encodedTC || textTC;

        const meta = {
            name: body('012') || 'Imported Tournament',
            location: body('022'),
            nation: (body('032') || '').toUpperCase(),
            startDate: this.parseDate(body('042')),
            endDate: this.parseDate(body('052')),
            chiefArbiter: this.parseArbiter(body('102')),
            chiefArbiterFideId: this.parseArbiterId(body('102')),
            timeControl,
            format: typeText.format || this.formatFromTypeCode(body('192')) || 'swiss',
            type: typeText.type || this.typeFromTimeControl(timeControl) || 'standard',
            pointSystem: this.parseScoringSystem(records['162']) || '1-0.5-0',
            seedColor: { W: 'white', B: 'black' }[(body('152') || '').toUpperCase()] || '',
            tiebreaks: this.parseTiebreaks(body('212') || body('202')),
            programId: body('182')
        };

        // ---- rounds ---------------------------------------------------------
        const roundCount = Math.max(0, ...players.map(p => p.cells.length));
        const declared = this.int(body('142'), 0);
        meta.numRounds = Math.max(declared, roundCount) || roundCount;
        if (declared && declared < roundCount) {
            warnings.push(`The file declares ${declared} round(s) but contains results for ${roundCount}; the larger number was kept.`);
        }

        const rounds = [];
        for (let ri = 0; ri < roundCount; ri++) {
            const pairings = [];
            const done = new Set();

            players.forEach(p => {
                const cell = p.cells[ri];
                if (!cell) return;

                const isBye = !cell.oppNo || cell.colour === '-';
                if (isBye) {
                    const kind = this.outcome(cell.result);
                    // A zero-point bye means the player simply did not play. The app
                    // represents that by their absence from the round, which is also what
                    // trf-export.js turns back into a 'Z' — so it round-trips either way,
                    // and leaving it out keeps the round list free of empty rows.
                    if (kind === 'win' || kind === 'forfeitWin') {
                        pairings.push({ white: p.id, black: 'WO', result: '1-0' });
                    } else if (kind === 'draw' || kind === 'halfBye') {
                        pairings.push({ white: p.id, black: 'WO', result: '0.5-0.5' });
                    }
                    return;
                }

                const opp = byStartNo[cell.oppNo];
                if (!opp) {
                    warnings.push(`Round ${ri + 1}: ${this.label(p)} is listed against starting number ${cell.oppNo}, which no player has. That game was skipped.`);
                    return;
                }
                const key = [p.startNo, opp.startNo].sort((a, b) => a - b).join('-');
                if (done.has(key)) return;
                done.add(key);

                const oppCell = opp.cells[ri];
                if (!oppCell || oppCell.oppNo !== p.startNo) {
                    warnings.push(`Round ${ri + 1}: ${this.label(p)} is paired with ${this.label(opp)}, but ${this.label(opp)}'s line does not point back. The game was kept as ${this.label(p)} recorded it.`);
                }

                let white = p, black = opp, whiteCell = cell, blackCell = oppCell;
                if (cell.colour === 'b') {
                    white = opp; black = p; whiteCell = oppCell; blackCell = cell;
                } else if (cell.colour !== 'w') {
                    warnings.push(`Round ${ri + 1}: no colour recorded for ${this.label(p)} against ${this.label(opp)}; the lower starting number was taken as White.`);
                    if (opp.startNo < p.startNo) { white = opp; black = p; whiteCell = oppCell; blackCell = cell; }
                } else if (oppCell && oppCell.colour === cell.colour) {
                    warnings.push(`Round ${ri + 1}: ${this.label(p)} and ${this.label(opp)} are both recorded with the same colour. ${this.label(p)}'s line was followed.`);
                }

                pairings.push({
                    white: white.id,
                    black: black.id,
                    result: this.resultFor(whiteCell && whiteCell.result, blackCell && blackCell.result)
                });
            });

            pairings.forEach((pair, i) => { pair.table = i + 1; });
            rounds.push({ roundNo: ri + 1, pairings });
        }

        this.checkStatedPoints(players, rounds, meta, warnings);

        players.forEach(p => { delete p.cells; delete p.statedPoints; });
        return { meta, players, rounds, warnings };
    },

    label(p) {
        const name = [p.lastName, p.givenName].filter(Boolean).join(', ');
        return name || ('#' + p.startNo);
    },

    /**
     * Compare the score each player's line states against the score their results imply.
     * The app's own total always wins — importing a number that contradicts the games it
     * came from would put the standings permanently at odds with the crosstable — but a
     * disagreement is worth saying out loud, because it usually means the file was hand
     * edited or the two programs score byes differently.
     */
    checkStatedPoints(players, rounds, meta, warnings) {
        if (!window.SwissEngine || !window.SwissEngine.calculateStats) return;
        let stats;
        try {
            stats = window.SwissEngine.calculateStats(players, rounds, meta.pointSystem || '1-0.5-0');
        } catch (e) {
            return; // scoring is the app's business; a failure here must not block the import
        }
        const off = [];
        players.forEach(p => {
            if (!Number.isFinite(p.statedPoints)) return;
            const computed = stats[p.id] ? stats[p.id].score : 0;
            if (Math.abs(computed - p.statedPoints) > 1e-9) {
                off.push(`${this.label(p)} (file ${p.statedPoints}, results give ${computed})`);
            }
        });
        if (off.length) {
            warnings.push(
                `The score in the file does not match the results for ${off.length} player(s): ` +
                off.slice(0, 5).join('; ') + (off.length > 5 ? '; …' : '') +
                '. The app recalculated every score from the games.'
            );
        }
    },

    /**
     * Today's date in Norway, 'YYYY-MM-DD' — the start date of a file that has none.
     * The same rule as js/utils/oslo-date.js, which this classic script cannot import:
     * just after midnight UTC's date is still yesterday here.
     */
    osloToday(now = Date.now()) {
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/Oslo', year: 'numeric', month: '2-digit', day: '2-digit'
        }).formatToParts(new Date(now));
        const get = (type) => (parts.find(p => p.type === type) || {}).value || '';
        return `${get('year')}-${get('month')}-${get('day')}`;
    },

    /**
     * Parse, then shape the result the way the app stores a tournament — the same record
     * importTournamentFromFile() builds from a TRX, so both import paths hand the caller
     * something indistinguishable from a tournament that was created here.
     */
    toTournament(text) {
        const { meta, players, rounds, warnings } = this.parse(text);
        const id = Date.now().toString() + '_' + Math.floor(Math.random() * 1000);
        const date = meta.startDate || this.osloToday();

        // A TRF has ONE rating column, and it holds the rating this tournament was played
        // at. Which of the app's flat rating fields that is depends on the rate of play, so
        // a rapid event's ratings land in rapidRating and are found again by the same
        // `type`. Youth events are not rated at all; their numbers still seed the pairing,
        // so they go in the standard field, which is where the fallback looks first.
        // See js/utils/player-ratings.js on why these flat fields are what an import writes.
        const ratingField = { rapid: 'rapidRating', blitz: 'blitzRating' }[meta.type] || 'standardRating';
        players.forEach(p => {
            p.club = '';
            p.nsfId = '';
            p.status = true;          // present; the file records games, not availability
            p.kFactor = '';           // '' means "use the rating engine's default"
            p.standardRating = 0;
            p.rapidRating = 0;
            p.blitzRating = 0;
            p[ratingField] = p.rating;
        });

        return {
            tournament: {
                id,
                name: meta.name,
                date,
                format: meta.format,
                playerCount: players.length,
                roundCount: rounds.length,
                data: {
                    id,
                    name: meta.name,
                    startDate: meta.startDate,
                    endDate: meta.endDate || meta.startDate,
                    location: meta.location,
                    nation: meta.nation,
                    format: meta.format,
                    type: meta.type,
                    rounds: meta.numRounds,
                    roundsCount: meta.numRounds,
                    pointSystem: meta.pointSystem,
                    seedColor: meta.seedColor,
                    tiebreaks: meta.tiebreaks.length ? meta.tiebreaks : ['bh', 'bh1', 'de'],
                    chiefArbiter: meta.chiefArbiter,
                    chiefArbiterFideId: meta.chiefArbiterFideId,
                    timeControl: meta.timeControl,
                    date
                },
                players,
                rounds
            },
            warnings
        };
    }
};

window.TRFImport = TRFImport;
